import {
  QuotationTaskSearchCondition,
  useBackofficeSearchCondition,
} from '@/providers/BackofficeSearchConditionProvider';
import { useEffect, useState } from 'react';
import { MultiValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { useQuotationTaskPhases } from '@/hooks/api/1430_quotation_task_phases';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useWorkspaceUsers } from '@/hooks/api/0150_workspace_users';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useMenuListedTeams } from '@/hooks/api/2260_menu_listed_teams';

export const FilterModal = () => {
  const {
    quotationTaskDisplayType: displayType,
    quotationTaskSearchCondition: searchCondition,
    setQuotationTaskSearchCondition: setSearchCondition,
  } = useBackofficeSearchCondition();

  const [condition, setCondition] = useState<QuotationTaskSearchCondition>({ ...searchCondition });

  useEffect(() => {
    setCondition({ ...searchCondition });
  }, [searchCondition]);

  const onSubscribedChange = () => {
    setCondition((c) => ({ ...c, subscribed: !c.subscribed }));
  };

  const onPhasesChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, quotation_task_phase_ids: d.map((a) => a.value) }));
  };

  const onUsersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, user_ids: d.map((a) => a.value) }));
  };

  const onBusinessPartnersChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, business_partner_ids: d.map((a) => a.value) }));
  };

  const onAgenciesChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, agency_ids: d.map((a) => a.value) }));
  };

  const onTeamsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, team_ids: d.map((a) => a.value) }));
  };

  const onArchivedChange = () => {
    setCondition((c) => ({ ...c, archived: !c.archived }));
  };

  const onApplyClick = () => {
    setSearchCondition((_) => ({ ...condition, page: 1 }));
  };

  return (
    <div
      className="modal fade"
      id="modal-filter"
      role="dialog"
      tabIndex={-1}
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                フィルタ
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="subscribed"
                      checked={condition.subscribed}
                      onChange={onSubscribedChange}
                    />
                    <label className="form-check-label" htmlFor="subscribed">
                      マイリストに登録されたタスクのみ表示
                    </label>
                  </div>
                </div>
                {displayType == 'list' && (
                  <div className="form-group">
                    <label>フェーズ</label>
                    <PhasesSelect onChange={onPhasesChange} selectedIds={condition.quotation_task_phase_ids} />
                  </div>
                )}
                <div className="form-group">
                  <label>タスク担当者</label>
                  <UsersSelect onChange={onUsersChange} selectedIds={condition.user_ids} />
                </div>
                <div className="form-group">
                  <label>取引先</label>
                  <BusinessPartnersSelect
                    onChange={onBusinessPartnersChange}
                    selectedIds={condition.business_partner_ids}
                  />
                </div>
                <div className="form-group">
                  <label>代理店</label>
                  <BusinessPartnersSelect onChange={onAgenciesChange} selectedIds={condition.agency_ids} />
                </div>
                <div className="form-group">
                  <label>チーム</label>
                  <TeamsSelect onChange={onTeamsChange} selectedIds={condition.team_ids} />
                </div>
                <div className="form-group">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="archived"
                      checked={condition.archived}
                      onChange={onArchivedChange}
                    />
                    <label className="form-check-label" htmlFor="archived">
                      アーカイブ済タスクを含める
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                閉じる
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PhasesSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { data } = useQuotationTaskPhases();
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const UsersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useWorkspaceUsers({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.map((d): SelectOptionType => ({ value: d.id, label: d.user_account.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const BusinessPartnersSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useBusinessPartners({ identifier: _w_!.identifier, display_type: 'sales', page: 1, per: 1000 });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.business_partners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};

const TeamsSelect = ({
  onChange,
  selectedIds,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedIds: number[];
}) => {
  const { _w_ } = useCurrentWorkspace();
  const { data } = useMenuListedTeams({ identifier: _w_!.identifier });
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    if (data) setOptions(data.sales_order_teams.map((d): SelectOptionType => ({ value: d.id, label: d.name })));
  }, [data]);

  if (options == null) return <></>;

  return <SelectMulti options={options} selectedIds={selectedIds} onChange={onChange} />;
};
